import React, { useState } from 'react';

import type {
  AlineTextFormats,
  DeleteEdit,
  DocumentElement,
  EditEntry,
  EditEntryOrGroup,
  InsertEdit,
  InsertInstructionFieldEdit,
  PageDimensions,
  RemoveInstructionFieldEdit,
  TextFormatEdit,
} from '@aline/common';
import {
  assertNeverSafely,
  isEditEntryGroup,
  isParagraphElement,
  isParagraphGlyphEdit,
  isUUIDV4,
  polarityOfEntries,
  unionRangeOfEntries,
} from '@aline/common';
import type { CardProps } from '@mui/material';
import { Card, CardContent, CardHeader, IconButton, Typography, useTheme } from '@mui/material';
import { isEmpty, keys } from 'ramda';
import { Editor } from 'slate';
import { AlineAvatar, AlineTooltip } from 'src/components';
import { CheckIcon } from 'src/components/icons/CheckIcon';
import { RejectChangeXIcon } from 'src/components/icons/RejectChangeXIcon';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { getEntityDisplayDetails, useDocumentParams } from 'src/helpers';
import { useAuthorizedTransforms, useGetDocumentUser } from 'src/hooks';
import { useMaybeEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useDeepCompareMemo } from 'use-deep-compare';

import { getParagraphFormatDisplayLabel } from './get-paragraph-format-display-label';

const useStyles = makeStyles<StyleProps>()((theme, { activeEdit, buttonsDisabled }) => ({
  bodyText: {
    ...getTypographyForSx(theme.typography.caption),
  },
  button: {
    color: buttonsDisabled ? 'unset' : theme.appColors.primary,
  },
  card: {
    // Provide a transparent border in the non-active case so content inside the box doesn't jump when the border becomes active.
    border: activeEdit ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
    boxSizing: 'border-box',
    cursor: 'pointer',
    margin: theme.spacing(1),
  },
  cardAction: {
    alignSelf: 'center',
    margin: 0,
  },
  cardHeader: {
    padding: theme.spacing(1),
  },
  cardHeaderContent: {
    /**
     * By default flex containers don't shrink below their content width.  So, to make this container work with no-wrap text, we set minWidth to 0
     * so the container resizes correctly.
     */
    minWidth: 0,
  },
  editTextWrapper: {
    marginBottom: theme.spacing(1),
  },
  importedSubtitle: {
    fontSize: 12,
    fontStyle: 'italic',
    fontWeight: 'lighter',
  },
  titleText: {
    ...theme.appTypography.fieldLabel,
  },
}));

/**
 * For a single-polarity group, returns a synthetic `EditEntry` spanning every
 * member, typed `insertNode`/`deleteNode` so `useGetEditDetails` renders the
 * combined text content. Returns null otherwise.
 */
function makePolaritySuperGroupEntry(entries: EditEntry[]): EditEntry | null {
  if (entries.length < 2) return null;

  const polarity = polarityOfEntries(entries);
  if (!polarity) return null;

  const range = unionRangeOfEntries(entries);
  const { authorId, createdAt, groupId, updatedAt } = entries[0].edit;

  const edit: InsertEdit | DeleteEdit =
    polarity === 'insert'
      ? { authorId, createdAt, groupId, type: 'insertNode', updatedAt, value: {} }
      : { authorId, createdAt, groupId, type: 'deleteNode', updatedAt, value: {} };

  return { edit, range };
}

function getTextFormatBody(editValue: TextFormatEdit | Partial<AlineTextFormats>): string {
  const displayKeys = keys(editValue).map((formatKey): string => {
    switch (formatKey) {
      case 'fontColor':
        return 'color';
      case 'fontSize':
        return 'font size';
      case 'fontType':
        return 'font type';
      case 'verticalAlign':
        return (
          ('verticalAlign' in editValue && editValue['verticalAlign']?.align) || 'vertical align'
        );
      default:
        return formatKey;
    }
  });

  return joinMaybeNullKeys(displayKeys);
}

interface StyleProps {
  activeEdit: boolean;
  buttonsDisabled: boolean;
}

interface TrackedChangesBoxProps extends CardProps {
  activeEdit: boolean;
  editEntry: EditEntryOrGroup;
}

interface EditTitle {
  primaryTitle: string;
  secondaryTitle?: string;
}

interface EditDetails {
  primaryBody: string;
  primaryTitle: string;
  secondaryBody?: string;
  secondaryTitle?: string;
}

const maxChars = 250;

export function useCanAcceptRejectChanges() {
  const editingModeRes = useEditingMode();

  return {
    ...editingModeRes,
    canAcceptRejectChanges: editingModeRes.suggesting || editingModeRes.editing,
  };
}

export function TrackedChangesBox({
  activeEdit,
  editEntry,
}: TrackedChangesBoxProps): JSX.Element | null {
  const theme = useTheme();
  const editor = useMaybeEditor();
  const {
    derived: { setSuggestedEdits },
  } = useEditorTabStateActions();
  const currentUserId = useCurrentUserId();
  const [disabled, setDisabled] = useState<boolean>(true);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { canAcceptRejectChanges, editingMode } = useCanAcceptRejectChanges();
  const { classes } = useStyles({ activeEdit, buttonsDisabled: disabled });
  const isGroup = isEditEntryGroup(editEntry);
  const representativeEntry = isGroup ? editEntry.editEntries[0] : editEntry;
  const {
    author,
    isChangeFromImportedDocument,
    name: authorName,
  } = useGetEditAuthor(representativeEntry.edit.authorId);

  const polaritySyntheticEntry = isGroup
    ? makePolaritySuperGroupEntry(editEntry.editEntries)
    : null;
  const entryForDetails = polaritySyntheticEntry ?? representativeEntry;
  const entryForScroll = isGroup
    ? { ...representativeEntry, range: unionRangeOfEntries(editEntry.editEntries) }
    : editEntry;

  const editDetails = useGetEditDetails(entryForDetails, isGroup ? editEntry.editEntries : null);
  const { primaryBody, primaryTitle, secondaryBody, secondaryTitle } =
    isGroup && !polaritySyntheticEntry
      ? {
          primaryBody: '',
          primaryTitle: editEntry.groupLabel,
          secondaryBody: undefined,
          secondaryTitle: undefined,
        }
      : editDetails;
  const isAcceptRejectDisabled = !canAcceptRejectChanges || disabled;

  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);
  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);

  /**
   * Note: The IconButtons below are wrapped in spans to clear an error by Material-UI that we are using a Tooltip with
   * a disabled button.  The Material-UI error suggested simply wrapping the button in a span to remove the error.
   */
  return (
    <Card
      className={classes.card}
      raised={!disabled && !activeEdit}
      onClick={(): void => {
        if (!editor) return;

        ClientTransforms.scrollToChange(editor, {
          editEntry: entryForScroll,
          rWHeightPx,
          rWOffsetPx,
        });
        ClientTransforms.focus(editor);
      }}
      onMouseEnter={(): void => setDisabled(false)}
      onMouseLeave={(): void => setDisabled(true)}
    >
      <CardHeader
        action={
          <>
            <AlineTooltip arrow placement="top" title="Reject change">
              <span>
                <IconButton
                  className={classes.button}
                  color="primary"
                  disabled={isAcceptRejectDisabled}
                  size="small"
                  onClick={(event: React.MouseEvent): void => {
                    event.stopPropagation();

                    if (!editingMode || !currentUserId || !editor) return;

                    if (isGroup) {
                      AuthorizedTransforms.rejectEditGroup(editor, {
                        authorId: currentUserId,
                        editGroup: editEntry,
                        editingMode,
                      });
                    } else {
                      AuthorizedTransforms.rejectSuggestedEdit(editor, {
                        authorId: currentUserId,
                        editEntry,
                        editingMode,
                      });
                    }

                    // Must be after transform to get correct value.
                    const newEditEntries = Queries.sortedEditRanges(editor);
                    setSuggestedEdits(newEditEntries);
                    ClientTransforms.focus(editor);
                  }}
                >
                  <RejectChangeXIcon
                    fill={!isAcceptRejectDisabled ? 'rgb(203, 62, 62)' : undefined}
                  />
                </IconButton>
              </span>
            </AlineTooltip>
            <AlineTooltip arrow placement="top" title="Accept change">
              <span>
                <IconButton
                  className={classes.button}
                  color="primary"
                  disabled={isAcceptRejectDisabled}
                  size="small"
                  onClick={(event: React.MouseEvent): void => {
                    event.stopPropagation();

                    if (!editingMode || !currentUserId || !editor) return;

                    if (isGroup) {
                      AuthorizedTransforms.acceptEditGroup(editor, {
                        authorId: currentUserId,
                        editGroup: editEntry,
                        editingMode,
                      });
                    } else {
                      AuthorizedTransforms.acceptSuggestedEdit(editor, {
                        authorId: currentUserId,
                        editEntry,
                        editingMode,
                      });
                    }

                    // Must be after transform to get correct value.
                    const newEditEntries = Queries.sortedEditRanges(editor);
                    setSuggestedEdits(newEditEntries);
                    ClientTransforms.focus(editor);
                  }}
                >
                  <CheckIcon fill={!isAcceptRejectDisabled ? theme.appColors.accent2 : undefined} />
                </IconButton>
              </span>
            </AlineTooltip>
          </>
        }
        avatar={<AlineAvatar avatarInfo={author ?? authorName.charAt(0).toUpperCase()} />}
        classes={{
          action: classes.cardAction,
          content: classes.cardHeaderContent,
          root: classes.cardHeader,
        }}
        title={authorName}
        titleTypographyProps={{
          classes: { root: classes.titleText },
          noWrap: true,
        }}
      />
      <CardContent>
        <div className={classes.editTextWrapper}>
          <Typography
            className={classes.titleText}
            sx={{
              display: 'inline',
            }}
          >
            {primaryTitle}
          </Typography>
          <Typography
            sx={{
              display: 'inline',
            }}
            variant="caption"
          >
            {primaryBody}
          </Typography>
        </div>
        {secondaryTitle && secondaryBody && (
          <div className={classes.editTextWrapper}>
            <Typography
              className={classes.titleText}
              sx={{
                display: 'inline',
              }}
            >
              {secondaryTitle}
            </Typography>
            <Typography
              sx={{
                display: 'inline',
              }}
              variant="caption"
            >
              {secondaryBody}
            </Typography>
          </div>
        )}
        {isChangeFromImportedDocument && (
          <Typography className={classes.importedSubtitle} variant="subtitle2">
            From imported document
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Hook that returns user info for tracked changes balloons.  If the authorId is not a UUID, that
 * means the edit was imported by our DOCX parser.  If the authorId is a UUID, the edit originated
 * from an Aline user within Aline.
 */
function useGetEditAuthor(authorId: string): {
  author: ReturnType<typeof useGetDocumentUser>['user'];
  isChangeFromImportedDocument: boolean;
  name: string;
} {
  const isUserId = isUUIDV4(authorId);
  const { documentId } = useDocumentParams();
  const { user } = useGetDocumentUser(documentId, isUserId ? authorId : null);
  const userName = user ? getEntityDisplayDetails(user).primary : '';

  return {
    author: user,
    isChangeFromImportedDocument: !isUserId,
    name: isUserId ? userName : authorId,
  };
}

function useGetEditDetails(editEntry: EditEntry, groupEditEntries: EditEntry[] | null) {
  const editor = useMaybeEditor();

  /**
   * Note: Memoizing this *avoids a runtime error*.  The error occurs when the component re-renders *before* the
   * new set of EditEntries is calculated (b/c that calculation is debounced).
   */
  return useDeepCompareMemo((): EditDetails => {
    if (editor && !Queries.hasRange(editor, editEntry.range)) {
      return { primaryBody: '', primaryTitle: getEditTitle(editEntry).primaryTitle };
    }

    switch (editEntry.edit.type) {
      case 'insertDrawing':
      case 'removeDrawing': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: '', primaryTitle };
      }
      case 'insertNode':
      case 'deleteNode': {
        const { edit, range } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);
        if (!editor) return { primaryBody: '', primaryTitle };

        const editedContent = Queries.string(editor, { at: range, voids: true });

        /**
         * Note: We tried various text truncation libraries to truncate long text for insert/delete node, but none of them worked well
         * since we need to have a title *and* the inserted/deleted content.  So, the mechanism/hack below should work well for now
         * and it looks pretty good.
         */
        if (!isEmpty(editedContent)) {
          const overLimit = editedContent.length >= maxChars;
          const body = overLimit ? `${editedContent.slice(0, maxChars)}...` : editedContent;
          return { primaryBody: `"${body}"`, primaryTitle };
        }

        /**
         * Super-group path: count glyph members in `groupEditEntries` for multiple grouped paragraph
         * insertions or deletions
         */
        const glyphCount = groupEditEntries?.filter(e => isParagraphGlyphEdit(e.edit)).length ?? 0;
        if (glyphCount > 0) {
          const body = glyphCount > 1 ? `(${glyphCount} times)` : `(${glyphCount} time)`;
          return {
            primaryBody: body,
            primaryTitle: edit.type === 'insertNode' ? 'Insert paragraph: ' : 'Delete paragraph: ',
          };
        }

        const acrossParagraphs = Queries.rangeAcrossParagraphs(editor, { at: range });
        if (!acrossParagraphs) return { primaryBody: `"${editedContent}"`, primaryTitle };

        const paragraphEntries = [...Queries.paragraphsAtLocation(editor, { at: range })];
        const numParagraphBreaks = paragraphEntries.length - 1;
        const body =
          numParagraphBreaks > 1 ? `(${numParagraphBreaks} times)` : `(${numParagraphBreaks} time)`;

        return {
          primaryBody: body,
          primaryTitle: edit.type === 'insertNode' ? 'Insert paragraph: ' : 'Delete paragraph: ',
        };
      }
      case 'insertParagraph':
      case 'deleteParagraph': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: '(1 time)', primaryTitle };
      }
      case 'insertBreak':
      case 'removeBreak': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: 'page break', primaryTitle };
      }
      case 'insertTab':
      case 'removeTab': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: 'tab', primaryTitle };
      }
      case 'insertBookmark': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: 'bookmark', primaryTitle };
      }
      case 'paragraphFormat': {
        const { edit, range } = editEntry;
        const { primaryTitle, secondaryTitle } = getEditTitle(editEntry);
        const { listProps, ...rest } = edit.value;

        const displayKeys = keys(rest).map(getParagraphFormatDisplayLabel);

        // Use === undefined since listProps can be null in a suggestion.
        if (listProps === undefined || !secondaryTitle)
          return { primaryBody: joinMaybeNullKeys(displayKeys), primaryTitle };

        if (!listProps)
          return {
            primaryBody: joinMaybeNullKeys(displayKeys),
            primaryTitle,
            secondaryBody: 'remove from list',
            secondaryTitle,
          };

        if (!editor)
          return {
            primaryBody: joinMaybeNullKeys(displayKeys),
            primaryTitle,
            secondaryBody: 'list',
            secondaryTitle,
          };

        const paragraphEntries = [
          ...Editor.nodes(editor, { at: range, match: isParagraphElement }),
        ];
        const isAddToLisEdit = paragraphEntries.every(([paragraph]) => !paragraph.listProps);

        if (isAddToLisEdit)
          return {
            primaryBody: joinMaybeNullKeys(displayKeys),
            primaryTitle,
            secondaryBody: 'add to list',
            secondaryTitle,
          };

        return {
          primaryBody: joinMaybeNullKeys(displayKeys),
          primaryTitle,
          secondaryBody: 'change list level',
          secondaryTitle,
        };
      }
      case 'variant': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);

        switch (edit.value.variant) {
          case 'style-normal':
            return { primaryBody: 'normal', primaryTitle };
          case 'style-title':
            return { primaryBody: 'title', primaryTitle };
          case 'style-subtitle':
            return { primaryBody: 'subtitle', primaryTitle };
          case 'style-heading1':
            return { primaryBody: 'heading 1', primaryTitle };
          case 'style-heading2':
            return { primaryBody: 'heading 2', primaryTitle };
          case 'style-heading3':
            return { primaryBody: 'heading 3', primaryTitle };
          default:
            return { primaryBody: 'unknown', primaryTitle };
        }
      }
      case 'pageDimensions': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);
        const dimensionsKeys: (keyof PageDimensions)[] = keys(edit.value.pageDimensions ?? {});

        const displayKeys = dimensionsKeys.map((dimensionKey): string => {
          switch (dimensionKey) {
            case 'marginLeft':
              return 'margin left';
            case 'marginRight':
              return 'margin right';
            case 'marginTop':
              return 'margin top';
            case 'marginBottom':
              return 'margin bottom';
            case 'width':
              return 'page width';
            case 'height':
              return 'page height';
          }
        });

        return { primaryBody: joinMaybeNullKeys(displayKeys), primaryTitle };
      }
      case 'styleSheet': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);
        const styleVariantKeys: (keyof DocumentElement['styleSheet'])[] = keys(
          edit.value.styleSheet
        );

        const displayKeys = styleVariantKeys.map((styleVariant): string | null => {
          switch (styleVariant) {
            case 'style-heading1':
              return 'heading 1';
            case 'style-heading2':
              return 'heading 2';
            case 'style-heading3':
              return 'heading 3';
            case 'style-normal':
              return 'normal';
            case 'style-title':
              return 'title';
            case 'style-subtitle':
              return 'subtitle';
            default:
              return null;
          }
        });

        return { primaryBody: joinMaybeNullKeys(displayKeys), primaryTitle };
      }
      case 'textFormat': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: getTextFormatBody(edit.value), primaryTitle };
      }
      case 'insertLink': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);

        return {
          primaryBody: `with url "${edit.value.href}"`,
          primaryTitle,
        };
      }
      case 'removeLink': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);

        return {
          primaryBody: `with url "${edit.removedHref}"`,
          primaryTitle,
        };
      }
      case 'linkEdit': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);

        return {
          primaryBody: `"${edit.oldHref}" with "${edit.value.href}"`,
          primaryTitle,
        };
      }
      case 'pageFormat': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);
        const pageFormatKeys = keys(edit.value.headerFooter ?? {});

        const displayKeys = pageFormatKeys.map((pageFormatKey): string => {
          switch (pageFormatKey) {
            case 'diffFirstPage':
              return 'first-page header and footer';
            case 'evenOdd':
              return 'even-odd header and footer';
            case 'pageNumbering':
              return 'page numbering formatting';
          }
        });

        return { primaryBody: joinMaybeNullKeys(displayKeys), primaryTitle };
      }
      case 'headerFormat': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);
        const footerKeys = keys(edit.value);

        const displayKeys = footerKeys.map((footerKey): string => {
          switch (footerKey) {
            case 'headerMargin':
              return 'header margin';
          }
        });

        return { primaryBody: joinMaybeNullKeys(displayKeys), primaryTitle };
      }
      case 'footerFormat': {
        const { edit } = editEntry;
        const { primaryTitle } = getEditTitle(editEntry);
        const footerKeys = keys(edit.value);

        const displayKeys = footerKeys.map((footerKey): string => {
          switch (footerKey) {
            case 'footerMargin':
              return 'footer margin';
          }
        });

        return { primaryBody: joinMaybeNullKeys(displayKeys), primaryTitle };
      }
      case 'tableFormat': {
        const tableFormatKeys = keys(editEntry.edit.value);
        const displayKeys = tableFormatKeys.map(formatKey => {
          switch (formatKey) {
            case 'align':
              return 'align';
            case 'indent':
              return 'indent';
            case 'shade':
              return 'fill';
            case 'borders':
              return 'borders';
            default:
              return null;
          }
        });

        return {
          primaryBody: joinMaybeNullKeys(displayKeys),
          primaryTitle: 'Format table: ',
        };
      }
      case 'tableCellFormat': {
        const propKeys = keys(editEntry.edit.value);
        const displayKeys = propKeys.map(propKey => {
          switch (propKey) {
            case 'shade':
              return 'fill';
            case 'margins':
              return 'margins';
            default:
              return null;
          }
        });

        return {
          primaryBody: joinMaybeNullKeys(displayKeys),
          primaryTitle: 'Format table cell: ',
        };
      }
      case 'insertTableCell':
        return { primaryBody: '', primaryTitle: 'Insert table cell' };
      case 'removeTableCell':
        return { primaryBody: '', primaryTitle: 'Remove table cell' };
      case 'insertTableRow':
        return {
          primaryBody: '',
          primaryTitle:
            editor && Queries.isEntireTableOrColumnSuggestion(editor, editEntry)
              ? 'Insert table'
              : 'Insert table row',
        };
      case 'removeTableRow':
        return {
          primaryBody: '',
          primaryTitle:
            editor && Queries.isEntireTableOrColumnSuggestion(editor, editEntry)
              ? 'Delete table'
              : 'Delete table row',
        };
      case 'listFormat':
        return {
          primaryBody: 'list format',
          primaryTitle: getEditTitle(editEntry).primaryTitle,
        };
      case 'insertInstructionField':
      case 'removeInstructionField': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: '', primaryTitle };
      }
      case 'insertCheckbox':
      case 'removeCheckbox': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: '', primaryTitle };
      }
      case 'toggleCheckbox': {
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: editEntry.edit.value.to, primaryTitle };
      }
      default: {
        // Guard against new edit types but still provide a fallback.
        assertNeverSafely(editEntry.edit);
        const { primaryTitle } = getEditTitle(editEntry);
        return { primaryBody: 'edit', primaryTitle };
      }
    }
  }, [editEntry, groupEditEntries]);
}

function getEditTitle(editEntry: EditEntry): EditTitle {
  switch (editEntry.edit.type) {
    case 'insertDrawing':
      return { primaryTitle: 'Insert image' };
    case 'removeDrawing':
      return { primaryTitle: 'Delete image' };
    case 'insertCheckbox':
      return { primaryTitle: 'Insert checkbox' };
    case 'removeCheckbox':
      return { primaryTitle: 'Delete checkbox' };
    case 'toggleCheckbox':
      return { primaryTitle: 'Toggle checkbox: ' };
    case 'insertNode':
      return { primaryTitle: 'Insert: ' };
    case 'deleteNode':
      return { primaryTitle: 'Delete: ' };
    // Only reached for singular paragraph-glyph entries
    case 'insertParagraph':
      return { primaryTitle: 'Insert paragraph: ' };
    case 'deleteParagraph':
      return { primaryTitle: 'Delete paragraph: ' };
    case 'insertBreak':
      return { primaryTitle: 'Insert break: ' };
    case 'removeBreak':
      return { primaryTitle: 'Remove break: ' };
    case 'insertTab':
      return { primaryTitle: 'Insert tab: ' };
    case 'removeTab':
      return { primaryTitle: 'Remove tab: ' };
    case 'insertBookmark':
      return { primaryTitle: 'Insert bookmark: ' };
    case 'styleSheet':
      return { primaryTitle: 'Format text style: ' };
    case 'textFormat':
      return { primaryTitle: 'Text format: ' };
    case 'insertLink':
      return { primaryTitle: 'Insert link: ' };
    case 'removeLink':
      return { primaryTitle: 'Remove link: ' };
    case 'linkEdit':
      return { primaryTitle: 'Replace link: ' };
    case 'headerFormat':
      return { primaryTitle: 'Format headers: ' };
    case 'footerFormat':
      return { primaryTitle: 'Format footers: ' };
    case 'paragraphFormat': {
      if (!('listProps' in editEntry.edit.value)) return { primaryTitle: 'Format: ' };

      return { primaryTitle: 'Format: ', secondaryTitle: 'Format list: ' };
    }
    case 'tableFormat':
      return { primaryTitle: 'Format table: ' };
    case 'insertTableCell':
      return { primaryTitle: 'Insert table cell' };
    case 'removeTableCell':
      return { primaryTitle: 'Remove table cell' };
    case 'insertInstructionField':
      return getInsertInstructionFieldTitle(editEntry.edit);
    case 'removeInstructionField':
      return getRemoveInstructionFieldTitle(editEntry.edit);
    default:
      return { primaryTitle: 'Format: ' };
  }
}

function joinMaybeNullKeys(keys: (string | null | undefined)[]): string {
  return keys.filter(key => key).join(', ');
}

function getInsertInstructionFieldTitle(edit: InsertInstructionFieldEdit): EditTitle {
  switch (edit.instruction) {
    case 'PAGE':
      return { primaryTitle: 'Insert page number' };
    case 'NUMPAGES':
      return { primaryTitle: 'Insert number of pages' };
    case 'TOC':
      return { primaryTitle: 'Insert table of contents' };
    case 'REF':
    case 'PAGEREF':
    case 'NOTEREF':
      return { primaryTitle: 'Insert cross-reference' };
    default:
      assertNeverSafely(
        edit.instruction,
        `Unsupported instruction field variant: ${edit.instruction}.`
      );
      return { primaryTitle: 'Insert instruction field' };
  }
}

function getRemoveInstructionFieldTitle(edit: RemoveInstructionFieldEdit): EditTitle {
  switch (edit.instruction) {
    case 'PAGE':
      return { primaryTitle: 'Remove page number' };
    case 'NUMPAGES':
      return { primaryTitle: 'Remove number of pages' };
    case 'TOC':
      return { primaryTitle: 'Remove table of contents' };
    case 'REF':
    case 'PAGEREF':
    case 'NOTEREF':
      return { primaryTitle: 'Remove cross-reference' };
    default:
      assertNeverSafely(
        edit.instruction,
        `Unsupported instruction field variant: ${edit.instruction}.`
      );
      return { primaryTitle: 'Remove instruction field' };
  }
}
